// src/pages/Workforce/AttendancePage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import T from '../../components/common/T';
import { useAuth } from '../../context/AuthContext';
import { canManageWorkforce, getWorkforceId } from '../../utils/roleUtils';
import {
  Download, RefreshCw, MapPin, Loader2, LogIn, LogOut,
  Fingerprint, ShieldAlert, ChevronLeft, ChevronRight,
  LayoutGrid, List, Calendar
} from "lucide-react";
import api from '../../api/axiosConfig';
import { DayModal, GeofenceBadge } from './AttendenceShared';
import AttendanceGridView from './AttendanceGridView';
import AttendanceListView from './AttendanceListView';
import AttendanceCalendarView from './AttendenceCalenderView';
import ViewSwitcher from '../../components/common/ViewSwitcher';
import PageLayout, { PageHeader } from '../../components/common/PageLayout';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

export default function AttendancePage() {
  const { user }      = useAuth();
  const isAdmin       = canManageWorkforce(user);
  const myWorkforceId = getWorkforceId(user);

  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [gridData,     setGridData]     = useState([]);
  const [isLoading,    setIsLoading]    = useState(true);
  const [error,        setError]        = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [modalCell,    setModalCell]    = useState(null);
  const [modalEmpName, setModalEmpName] = useState("");

  const [todayRecord, setTodayRecord] = useState(null);
  const [isClocking,  setIsClocking]  = useState(false);
  const [clockError,  setClockError]  = useState(null);
  const [gpsStatus,   setGpsStatus]   = useState("idle");
  const [geoWarning,  setGeoWarning]  = useState(null);

  const [viewMode, setViewMode] = useState("grid");
  const [calendarEmpId, setCalendarEmpId] = useState("");

  const [listData,         setListData]         = useState([]);
  const [searchTerm,       setSearchTerm]       = useState("");
  const [statusFilter,     setStatusFilter]     = useState("All");
  const [dateFilter,       setDateFilter]       = useState(now.toISOString().split("T")[0]);
  const [monthFilter,      setMonthFilter]      = useState(now.toISOString().slice(0, 7));

  const daysInMonth = useMemo(() => new Date(year, month, 0).getDate(), [year, month]);

  const dayHeaders = useMemo(() => {
    const days = [];
    const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month - 1, d);
      days.push({ day: d, dayName: dayNames[date.getDay()] });
    }
    return days;
  }, [year, month, daysInMonth]);

  const monthLabel = useMemo(
    () => new Date(year, month - 1, 1).toLocaleString("default", { month: "long" }),
    [year, month]
  );

  const fetchGridData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const pad = (n) => String(n).padStart(2, "0");
      const monthStr = `${year}-${pad(month)}`;
      const url = isAdmin
        ? `/workforce/attendance/monthly?month=${monthStr}`
        : `/workforce/attendance/monthly?month=${monthStr}&workforce_id=${myWorkforceId}`;

      const res = await api.get(url);
      setGridData(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [year, month, isAdmin, myWorkforceId]);

  const fetchListData = useCallback(async () => {
    try {
      const url = isAdmin
        ? `/workforce/attendance/records?date=${dateFilter}`
        : `/workforce/attendance/records?workforce_id=${myWorkforceId}&month=${monthFilter}`;
      const res = await api.get(url);
      const dbData = res.data;
      
      if (Array.isArray(dbData)) {
          setListData(dbData.map(item => ({
            ...item,
            avatar: item.name ? item.name[0].toUpperCase() : "U",
          })));
      } else {
          setListData([]);
      }
    } catch (err) {
      setListData([]);
    }
  }, [isAdmin, dateFilter, monthFilter, myWorkforceId]);

  const fetchTodayRecord = useCallback(async () => {
    if (!myWorkforceId) return;
    try {
      const today = now.toISOString().split("T")[0];
      const res = await api.get(`/workforce/attendance/today?workforce_id=${myWorkforceId}&date=${today}`);
      if (res.data) setTodayRecord(res.data);
    } catch (err) {}
  }, [myWorkforceId]);

  useEffect(() => {
    fetchGridData();
    if (!isAdmin) fetchTodayRecord();
  }, [fetchGridData, fetchTodayRecord, isAdmin]);

  useEffect(() => {
    if (viewMode === "list") fetchListData();
  }, [viewMode, fetchListData]);

  useEffect(() => {
    if (isAdmin && gridData.length > 0 && !calendarEmpId) {
      setCalendarEmpId(String(gridData[0].id));
    }
  }, [gridData, isAdmin, calendarEmpId]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const getCoordinates = () => new Promise((resolve) => {
    if (!navigator.geolocation) {
      setGpsStatus("denied");
      resolve({ latitude: null, longitude: null });
      return;
    }
    setGpsStatus("fetching");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsStatus("ok");
        resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      },
      () => {
        setGpsStatus("denied");
        resolve({ latitude: null, longitude: null });
      },
      { timeout: 8000, enableHighAccuracy: true }
    );
  });

  const handleClock = async (type) => {
    setIsClocking(true);
    setClockError(null);
    setGeoWarning(null);
    try {
      const { latitude, longitude } = await getCoordinates();
      const res = await api.post('/workforce/attendance/mark', {
        employee_id: myWorkforceId,
        action: type === "IN" ? "clock_in" : "clock_out",
        clock_in_lat: latitude,
        clock_in_lng: longitude,
        location: "Main Construction Site",
        geofence_status: "verified",
      });
      const data = res.data || {};
      if (data.geofence_status === "flagged_out_of_range") {
        setGeoWarning("⚠️ You are outside the assigned site boundary. Your clock-in has been recorded but flagged for review.");
      }
      await fetchTodayRecord();
      await fetchGridData();
    } catch (err) {
      setClockError(err.response?.data?.message || err.response?.data?.error || err.message);
    } finally {
      setIsClocking(false);
    }
  };

  const stats = useMemo(() => {
    const counts = { present: 0, late: 0, half_day: 0, absent: 0, on_leave: 0 };
    gridData.forEach(emp => emp.days?.forEach(d => {
      if (counts[d.status] !== undefined) counts[d.status]++;
    }));
    return counts;
  }, [gridData]);

  const empAgg = useCallback((days = []) => ({
    P:  days.filter(d => d.status === "present").length,
    L:  days.filter(d => d.status === "late").length,
    HD: days.filter(d => d.status === "half_day").length,
    A:  days.filter(d => d.status === "absent").length,
    OL: days.filter(d => d.status === "on_leave").length,
  }), []);

  const clockedIn    = Boolean(todayRecord?.check_in_time);
  const clockedOut   = Boolean(todayRecord?.check_out_time);
  const attendanceDone = clockedIn && clockedOut;

  const filteredListData = useMemo(() => listData.filter(r => {
    const safeName = r.name || "";
    const s = isAdmin ? safeName.toLowerCase().includes((searchTerm || "").toLowerCase()) : true;
    let st = true;
    if (statusFilter !== "All") {
      const dbStatus = (r.status || "").toLowerCase().replace("_", " ");
      const filterVal = statusFilter.toLowerCase();
      st = (dbStatus === filterVal);
    }
    return s && st;
  }), [listData, searchTerm, statusFilter, isAdmin]);

  return (
    <PageLayout>
      {modalCell && (
        <DayModal cell={modalCell} employeeName={modalEmpName} onClose={() => { setModalCell(null); setModalEmpName(""); }} />
      )}

      <PageHeader
        icon={CalendarDaysIcon}
        iconColor="text-[#E8612C]"
        title={isAdmin ? "Monthly Attendance Register" : "My Attendance"}
        subtitle={isAdmin ? "Comprehensive attendance tracking and logs" : "Your personal monthly attendance tracking"}
        onRefresh={() => { setIsRefreshing(true); fetchGridData(); }}
        loading={isRefreshing || isLoading}
      >
        <div className="flex items-center bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-lg shadow-xs overflow-hidden h-8">
          <button onClick={prevMonth} className="px-2 h-full hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400 transition border-r border-gray-200 dark:border-[#30363d] cursor-pointer"><ChevronLeft size={16} /></button>
          <span className="px-3 text-xs font-bold text-gray-800 dark:text-white whitespace-nowrap">{monthLabel} {year}</span>
          <button onClick={nextMonth} disabled={year === now.getFullYear() && month === now.getMonth() + 1} className="px-2 h-full hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400 transition border-l border-gray-200 dark:border-[#30363d] disabled:opacity-30 cursor-pointer"><ChevronRight size={16} /></button>
        </div>

        <ViewSwitcher
          view={viewMode}
          onViewChange={setViewMode}
          availableViews={['grid', 'calendar', 'list']}
        />

        {isAdmin && (
          <button
            type="button"
            className="h-8 px-3 rounded-lg bg-[#E8612C] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#cf4f1d] transition shadow-2xs flex items-center gap-1.5 cursor-pointer"
          >
            <Download size={13} />
            <span className="hidden sm:inline">Export</span>
          </button>
        )}
      </PageHeader>

      {error && (
        <div className="bg-red-50 dark:bg-rose-950/40 border border-red-200 dark:border-rose-800/60 text-red-700 dark:text-rose-400 p-3 sm:p-4 rounded-lg mb-3 sm:mb-5 flex items-center justify-between shrink-0">
          <span className="text-sm"><strong>Error:</strong> {error}</span>
          <button onClick={fetchGridData} className="px-3 py-1 bg-white dark:bg-slate-800 border border-red-200 dark:border-slate-700 rounded text-xs font-bold hover:bg-red-50 dark:hover:bg-slate-700">Retry</button>
        </div>
      )}

      {/* CLOCK IN CARD */}
      {!isAdmin && (
        <div className={`mb-3 sm:mb-5 rounded-xl border p-3 sm:p-4 shadow-xs shrink-0 ${attendanceDone ? "bg-green-50 dark:bg-emerald-950/30 border-green-200 dark:border-emerald-800/60" : clockedIn ? "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/60" : "bg-white dark:bg-[#161b22] border-gray-200 dark:border-[#30363d]"}`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className={`p-2.5 sm:p-3 rounded-xl ${attendanceDone ? "bg-green-100 dark:bg-emerald-950/50 text-green-700 dark:text-emerald-400" : clockedIn ? "bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400" : "bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-400"}`}><Fingerprint size={22} /></div>
              <div>
                <p className="font-bold text-sm sm:text-base text-gray-900 dark:text-white">{attendanceDone ? "Today's Attendance Recorded" : clockedIn ? "You're Clocked In" : "Ready to Start Your Day?"}</p>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 mt-0.5">{attendanceDone ? `In: ${todayRecord.check_in_time}  ·  Out: ${todayRecord.check_out_time}` : clockedIn ? `Clocked in at ${todayRecord.check_in_time}` : new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
                {todayRecord?.geofence_status && <div className="mt-1"><GeofenceBadge status={todayRecord.geofence_status} /></div>}
                {gpsStatus === "fetching" && <p className="text-blue-600 text-xs mt-1 flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Acquiring GPS…</p>}
                {gpsStatus === "denied" && !clockError && <p className="text-orange-600 text-xs mt-1 flex items-center gap-1"><MapPin size={11} /> Location unavailable — attendance recorded without GPS.</p>}
                {geoWarning && <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs font-medium flex items-start gap-2"><ShieldAlert size={13} className="shrink-0 mt-0.5" />{geoWarning}</div>}
                {clockError && <p className="text-red-600 text-xs mt-1 font-medium">{clockError}</p>}
              </div>
            </div>
            {!attendanceDone && (
              <button onClick={() => handleClock(clockedIn ? "OUT" : "IN")} disabled={isClocking} className={`flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition shadow-sm disabled:opacity-60 whitespace-nowrap ${clockedIn ? "bg-red-600 hover:bg-red-700 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}`}>
                {isClocking ? <Loader2 size={16} className="animate-spin" /> : clockedIn ? <LogOut size={16} /> : <LogIn size={16} />}
                {isClocking ? "Processing…" : clockedIn ? "Clock Out" : "Clock In"}
              </button>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-gray-400" size={40} />
        </div>
      ) : viewMode === "calendar" ? (
        <AttendanceCalendarView 
          isAdmin={isAdmin} gridData={gridData} year={year} month={month} daysInMonth={daysInMonth}
          calendarEmpId={calendarEmpId} setCalendarEmpId={setCalendarEmpId} myWorkforceId={myWorkforceId}
          setModalCell={setModalCell} setModalEmpName={setModalEmpName}
        />
      ) : viewMode === "grid" ? (
        <AttendanceGridView 
          isAdmin={isAdmin} stats={stats} daysInMonth={daysInMonth} dayHeaders={dayHeaders} year={year} month={month}
          gridData={gridData} myWorkforceId={myWorkforceId} empAgg={empAgg} setModalCell={setModalCell}
          setModalEmpName={setModalEmpName} monthLabel={monthLabel}
        />
      ) : (
        <AttendanceListView 
          isAdmin={isAdmin} listData={listData} filteredListData={filteredListData}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm} statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          dateFilter={dateFilter} setDateFilter={setDateFilter} monthFilter={monthFilter} setMonthFilter={setMonthFilter}
        />
      )}
    </PageLayout>
  );
}